import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAddUserIdentity } from '@pocapan/shared/domains/user/ui/hooks/useAddUserIdentity';
import { getOneTimePaymentLaunchPath, getPgProvider } from '@pocapan/shared/utils/paymentProvider';
import type { PayLetterPaymentMethod } from '@pocapan/shared/domains/orders/services/orderServices';
import type {
  addUserIdentityBody,
  addUserIdentityResult
} from '@pocapan/shared/domains/user/services/userIdentities';
import { ROUTES } from '../../../constants/routes';
import { IDENTITY_VERIFICATION_COMPLETE } from '../constants/identityVerification';

const SCENE_GO_TO_SETTING_TIME = 1500;

const hasNestedError = (e: unknown): e is { error: { code: string; message: string } } => {
  return typeof e === 'object' && e !== null && 'error' in e;
};

export function useIdentityVerificationRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  const currentQueryParams = new URLSearchParams(location.search);
  const redirectPath = currentQueryParams.get('redirect_path');
  const impUid = currentQueryParams.get('imp_uid');
  const merchantUid = currentQueryParams.get('merchant_uid');
  const initialSuccess = currentQueryParams.get('success') === 'true';
  const hasRequiredParams = Boolean(impUid && merchantUid);
  const requiresServerValidation = initialSuccess && hasRequiredParams;

  const [isSuccess, setIsSuccess] = useState(initialSuccess);
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [isValidating, setIsValidating] = useState(requiresServerValidation);

  const { amount, productName, productId, isBilling, type, autoPay, productType, sceneId } =
    (() => {
      const topAmount = currentQueryParams.get('amount');
      const topProductName = currentQueryParams.get('productName');
      const topProductId = currentQueryParams.get('productId');
      const topIsBilling = currentQueryParams.get('isBilling');
      const topType = currentQueryParams.get('type');
      const topAutoPay = currentQueryParams.get('autoPay');
      const topProductType = currentQueryParams.get('productType');
      const topSceneId = currentQueryParams.get('sceneId');

      if (
        topAmount ||
        topProductName ||
        topProductId ||
        topType ||
        topAutoPay ||
        topProductType ||
        topSceneId
      ) {
        return {
          amount: topAmount,
          productName: topProductName,
          productId: topProductId,
          isBilling: topIsBilling === 'true',
          type: topType,
          autoPay: topAutoPay === 'true',
          productType: topProductType,
          sceneId: topSceneId
        };
      }

      if (redirectPath) {
        const decoded = decodeURIComponent(redirectPath);
        const url = new URL(decoded, window.location.origin);
        const sp = url.searchParams;

        return {
          amount: sp.get('amount'),
          productName: sp.get('productName'),
          productId: sp.get('productId'),
          isBilling: sp.get('isBilling') === 'true',
          type: sp.get('type'),
          autoPay: sp.get('autoPay') === 'true',
          productType: sp.get('productType'),
          sceneId: sp.get('sceneId')
        };
      }

      return {
        amount: null,
        productName: null,
        productId: null,
        type: null,
        isBilling: false,
        autoPay: false,
        productType: null,
        sceneId: null
      };
    })();

  const sceneProductQuery = `${productType ? `&productType=${encodeURIComponent(productType)}` : ''}${sceneId ? `&sceneId=${encodeURIComponent(sceneId)}` : ''}`;

  const { mutateAsync: addUserIdentity } = useAddUserIdentity();

  useEffect(() => {
    setIsSuccess(initialSuccess);
    setIsDuplicate(false);

    if (!requiresServerValidation) {
      setIsValidating(false);
      return;
    }

    let isCancelled = false;
    setIsValidating(true);

    (async () => {
      try {
        const { data, error }: addUserIdentityResult = await addUserIdentity({
          imp_uid: impUid,
          merchant_uid: merchantUid
        } as addUserIdentityBody);

        if (isCancelled) return;

        if (error) {
          setIsSuccess(false);
          const nestedCode = hasNestedError(error) ? error.error.code : undefined;
          const nestedMessage = hasNestedError(error) ? error.error.message : undefined;

          if (nestedCode === '1002') {
            setIsDuplicate(true);
          }
          if (nestedCode === 'RecordNotUnique' && nestedMessage === 'This key already identified') {
            setIsDuplicate(true);
          }
          return;
        }

        if (data?.userIdentity?.userIdentityId) {
          setIsSuccess(true);
          if (window.opener) {
            window.opener.postMessage({ type: IDENTITY_VERIFICATION_COMPLETE }, window.location.origin);
          }
          return;
        }

        setIsSuccess(false);
      } catch {
        if (!isCancelled) {
          setIsSuccess(false);
        }
      } finally {
        if (!isCancelled) {
          setIsValidating(false);
        }
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [
    initialSuccess,
    requiresServerValidation,
    impUid,
    merchantUid,
    addUserIdentity
  ]);

  useEffect(() => {
    if (type === 'scene' && isSuccess && !isValidating) {
      const timer = setTimeout(() => {
        if (redirectPath && window.opener) {
          window.close();
        } else if (redirectPath) {
          navigate(decodeURIComponent(redirectPath), { replace: true });
        } else {
          navigate(ROUTES.SCENE.SETTING, { replace: true });
        }
      }, SCENE_GO_TO_SETTING_TIME);
      return () => clearTimeout(timer);
    }
  }, [type, isSuccess, isValidating, navigate, redirectPath]);
  const isPayLetter = getPgProvider() === 'pay-letter';

  const handleClick = useCallback(
    async (paymentMethod?: PayLetterPaymentMethod) => {
      if (isSuccess) {
        if (type === 'scene') {
          return;
        }

        if (type === 'invite') {
          navigate('/free-charge/invite', { replace: true });
          return;
        }

        if (isBilling) {
          if (isPayLetter) {
            const oneTimePaymentLaunchPath = getOneTimePaymentLaunchPath();
            navigate(
              `${oneTimePaymentLaunchPath}?amount=${amount}&productName=${productName}&productId=${productId}&autoPay=true${sceneProductQuery}`
            );
            return;
          }
          navigate(
            `/payment/billing?amount=${amount}&productName=${productName}&productId=${productId}`
          );
          return;
        }
        const oneTimePaymentLaunchPath = getOneTimePaymentLaunchPath();
        navigate(
          `${oneTimePaymentLaunchPath}?amount=${amount}&productName=${productName}&productId=${productId}${paymentMethod ? `&paymentMethod=${paymentMethod}` : ''}${autoPay ? '&autoPay=true' : ''}${sceneProductQuery}`
        );
      } else {
        if (type === 'scene') {
          if (redirectPath && window.opener) {
            window.close();
          } else if (redirectPath) {
            navigate(decodeURIComponent(redirectPath), { replace: true });
          } else {
            navigate(ROUTES.SCENE.INTRO, { replace: true });
          }
          return;
        }
        navigate(redirectPath ? decodeURIComponent(redirectPath) : '/bettie-chat-list', {
          replace: true
        });
      }
    },
    [
      isSuccess,
      navigate,
      redirectPath,
      amount,
      productName,
      productId,
      isBilling,
      type,
      autoPay,
      isPayLetter,
      sceneProductQuery
    ]
  );

  const needsPayLetterMethodSelection =
    !isValidating &&
    isSuccess &&
    isPayLetter &&
    type !== 'invite' &&
    type !== 'scene' &&
    !isBilling &&
    !autoPay;

  return {
    isSuccess,
    hasRequiredParams,
    handleClick,
    isDuplicate,
    needsPayLetterMethodSelection,
    isValidating,
    type
  };
}
